/**
 * EncounterAuditsList Component
 *
 * Displays list of encounter audits with filtering and pagination
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { useEncounterAuditsList } from '../../hooks/useEncounterAuditsList';
import { NotesFilters, type NotesFiltersState } from '../notes/NotesFilters';

import { EncounterAuditRow } from './EncounterAuditRow';
import { EncounterAuditsTableHeader } from './EncounterAuditsTableHeader';
import type { TableLayoutMode } from './types';

interface EncounterAuditsListProps {
  /** Filter by specific user ID */
  filterByUserId?: string;
  /** Organization ID for admin mode */
  organizationId?: string;
}

export const EncounterAuditsList: React.FC<EncounterAuditsListProps> = ({
  filterByUserId,
  organizationId,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { jotauditDashboard } = useEventTracking();

  // Responsive table layout based on screen width
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));   // >= 1536px
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));   // >= 1200px
  const isMd = useMediaQuery(theme.breakpoints.up('md'));   // >= 900px

  const tableLayout: TableLayoutMode = useMemo(() => {
    if (isXl) return 'full';
    if (isLg) return 'standard';
    if (isMd) return 'compact';
    return 'card';
  }, [isXl, isLg, isMd]);

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<NotesFiltersState>({
    completionStatus: 'all',
  });
  const [isFilterChanging, setIsFilterChanging] = useState(false);
  const previousFiltersRef = useRef<string>(JSON.stringify(filters));

  // Convert userIds array to comma-separated string
  const userIdsParam = useMemo(() => {
    if (filterByUserId) return filterByUserId;
    if (filters.userIds && filters.userIds.length > 0) {
      return filters.userIds.join(',');
    }
    return undefined;
  }, [filterByUserId, filters.userIds]);

  const {
    encounters,
    total,
    page,
    perPage,
    hasMore,
    isLoading,
    isFetching,
    error,
    refetch,
    isEmpty,
  } = useEncounterAuditsList({
    organizationId,
    userIds: userIdsParam,
    page: currentPage,
    perPage: 20,
    days: filters.days,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });

  // Track filter changes for loading state
  useEffect(() => {
    const currentFiltersJson = JSON.stringify(filters);
    if (previousFiltersRef.current !== currentFiltersJson) {
      setIsFilterChanging(true);
      previousFiltersRef.current = currentFiltersJson;
    }
  }, [filters]);

  // Reset loading state when fetch completes
  useEffect(() => {
    if (isFilterChanging && !isFetching) {
      setIsFilterChanging(false);
    }
  }, [isFilterChanging, isFetching]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Calculate pagination values
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const hasPrevious = currentPage > 1;

  const handlePreviousPage = useCallback(() => {
    if (hasPrevious && !isLoading) {
      const newPage = Math.max(1, currentPage - 1);
      setCurrentPage(newPage);
    }
  }, [hasPrevious, isLoading, currentPage]);

  const handleNextPage = useCallback(() => {
    if (hasMore && !isLoading) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
    }
  }, [hasMore, isLoading, currentPage]);

  const handleViewEncounter = useCallback(
    (encounterId: string) => {
      if (organizationId) {
        navigate(`/admin-v2/jotaudit/${organizationId}/encounter/${encounterId}`);
      } else {
        navigate(`/jotaudit/encounter/${encounterId}`);
      }
    },
    [navigate, organizationId],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading encounters</AlertTitle>
        {error}
      </Alert>
    );
  }

  const showLoadingState = isLoading || isFilterChanging;

  return (
    <Box>
      {/* Filters */}
      {!filterByUserId && (
        <NotesFilters
          filters={filters}
          onFiltersChange={setFilters}
          isLoading={showLoadingState}
        />
      )}

      {/* Header */}
      {!filterByUserId && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Encounter Audits
            </Typography>
            {!showLoadingState && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {total} {total === 1 ? 'encounter' : 'encounters'}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Encounters Table */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <EncounterAuditsTableHeader layout={tableLayout} />

        {showLoadingState ? (
          <Box>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                }}
              >
                <Skeleton variant="rectangular" height={40} />
              </Box>
            ))}
          </Box>
        ) : isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.secondary }}>
              No encounters found
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              Try adjusting your filters or date range
            </Typography>
          </Box>
        ) : (
          <Box>
            {encounters.map(encounter => (
              <EncounterAuditRow
                key={encounter.encounter_id}
                encounter={encounter}
                onViewEncounter={handleViewEncounter}
                layout={tableLayout}
              />
            ))}
          </Box>
        )}

        {/* Pagination */}
        {!isEmpty && !showLoadingState && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${theme.palette.grey[200]}`,
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Page {page} of {totalPages} ({total} total)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handlePreviousPage}
                disabled={!hasPrevious || isLoading}
                size="small"
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={handleNextPage}
                disabled={!hasMore || isLoading}
                size="small"
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
